<script setup>
import { ref, computed, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const { $constants, $bus } = proxy;

const modalRef = ref(null);
const currentVideo = ref(null);
const isLandscape = ref(false);

const videoKey = computed(() => {
  if (!currentVideo.value) return '';
  const data = currentVideo.value;
  
  // Si c'est déjà un objet avec une clé (ancien format)
  if (typeof data === 'object' && data.key) return data.key;
  
  // Si c'est une URL string (nouveau format)
  if (typeof data === 'string') {
    const regExp = /^.*(youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/|live\/|e\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = data.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    return data; // Retourne la string telle quelle si c'est déjà l'ID
  }
  
  return '';
});

const videoUrl = computed(() => {
  if (!videoKey.value) return '';
  return `https://www.youtube.com/embed/${videoKey.value}?autoplay=1&rel=0&modestbranding=1&showinfo=0`;
});

const onPlayVideo = (data) => {
  console.log('🎬 VideoPlayerDialog received video data:', data);
  currentVideo.value = data;
  modalRef.value?.show();
};

const onClose = () => {
  $bus.emit($constants.EVENTS.VIDEO.CLOSE);
  currentVideo.value = null;
  isLandscape.value = false;
};

const toggleRotation = () => {
  isLandscape.value = !isLandscape.value;
};

defineExpose({
  show: () => modalRef.value?.show(),
  hide: () => modalRef.value?.hide()
});
</script>

<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.VIDEO.PLAY"
    :handler="onPlayVideo"
  />

  <DialogComponent
    ref="modalRef"
    type="fullscreen"
    :hide-close-button="true"
    :no-inner-padding="true"
    :no-overlay="true"
    @closed="onClose"
    
  >
    <div class="relative h-full w-full bg-black overflow-hidden">
      <div
        class="player absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
        :class="{
          'rotate-90 scale-50 ': isLandscape
        }"
        :style="{
          width: isLandscape ? 'calc(200vh - 200px)' : '100vw',
          height: isLandscape ? '200vw' : '100vh'
        }"
      >
        <iframe
          v-if="videoKey"
          :src="videoUrl"
          class="w-full h-full"
          frameborder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowfullscreen
        />
        <div class="absolute w-full h-[100px] bg-black  top-0 left-0"></div>
        <div class="absolute w-full h-[60px] bg-black  bottom-0 left-0"></div>
        <!-- <div
          class="absolute w-[60px] h-[60px] bg-black  bottom-[90px] right-0"
        ></div> -->
      </div>

      <!-- Rotation Button -->
      <button
        @click="toggleRotation"
        class="mt absolute bottom-[90px] right-4 z-[1000] flex items-center justify-center w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-all"
      >
        <svg
          class="w-6 h-6 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      </button>

      <!-- Close Button Flottant -->
      <button
        @click="modalRef?.hide()"
        class="mobile-status-padding-mt absolute top-4 right-4 z-[1000] flex items-center justify-center w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-all"
      >
        <svg
          class="w-6 h-6 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <!-- No Video State -->
      <div
        v-if="!videoKey"
        class="flex flex-col items-center justify-center h-full text-white/60"
      >
        <svg
          class="w-16 h-16 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
        <p class="text-lg">Vidéo non disponible</p>
      </div>
    </div>
  </DialogComponent>
</template>
